/**
 * License Panel (Schritt 3: Lizenz wählen)
 *
 * Erst ordnet der Admin jedem Benutzer aktiv eine SAIA-Lizenz zu (nichts ist
 * vorausgewählt), danach folgt die Zahlungsperiode. Zeigt vorhandene
 * Lizenzen und startet für neue Lizenzen den Stripe-Checkout (Kreditkarte
 * oder SEPA-Lastschrift; PayPal sobald in Stripe freigeschaltet). Jede neue Lizenz startet mit 14 Tagen Test — die
 * Zahlungsdaten werden sofort hinterlegt, nach dem Testzeitraum rechnet Stripe
 * automatisch ab.
 */

import React, { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import config from '../../../config';
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { API_V1 } from '../../../API/APIs/APIv1';
import useAIChatStore from '../../../stores/ai-chat-store';
import useUserStore from '../../../stores/user-store';
import { dc } from '../design-tokens';
import { LICENSE_OPTIONS } from './license-options';

interface LicensePanelProps {
  onSaved: (summary: string) => void;
  /** Der Workspace hat schon eine gültige SAIA-Lizenz — dann nur „Weiter", kein zweiter Checkout-Knopf. */
  done?: boolean;
  onNext?: () => void;
}

interface UserRow {
  id: string;
  name: string;
  email: string;
}

interface LicenseRow {
  id: string;
  product: string;
  linkedinAccountId: string | null;
  isExpired?: boolean;
}

type BillingPeriod = 'monthly' | 'quarterly' | 'yearly';

/** Monatlicher Basispreis pro Seat (muss zu den Stripe-Preisen passen). */
const MONTHLY_BASE: Record<string, number> = {
  saia_recruiting: 139.9,
  saia_sales: 139.9,
  saia_bundle: 249.9,
};

/** Rabatt nach Zahlungsperiode: Quartal -10 %, Jahr -20 %. */
const PERIOD_META: Array<{
  key: BillingPeriod;
  de: string;
  en: string;
  months: number;
  discount: number;
  badge?: string;
}> = [
  { key: 'monthly', de: 'Monatlich', en: 'Monthly', months: 1, discount: 0 },
  { key: 'quarterly', de: 'Quartalsweise', en: 'Quarterly', months: 3, discount: 0.1, badge: '-10 %' },
  { key: 'yearly', de: 'Jährlich', en: 'Yearly', months: 12, discount: 0.2, badge: '-20 %' },
];

const euro = (n: number) =>
  n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

// onSaved wird aktuell nicht genutzt — der Checkout läuft inline und der Status
// aktualisiert sich über dataVersion; die Prop bleibt für die Workspace-API erhalten.
const LicensePanel: React.FC<LicensePanelProps> = ({ done, onNext }) => {
  const { classes, cx } = useStyles();
  const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';
  const { dataVersion, licenseAssignments, setLicenseAssignment, setSidePanel, bumpDataVersion } = useAIChatStore(
    (state) => ({
      dataVersion: state.dataVersion,
      licenseAssignments: state.licenseAssignments,
      setLicenseAssignment: state.setLicenseAssignment,
      setSidePanel: state.setSidePanel,
      bumpDataVersion: state.bumpDataVersion,
    })
  );
  const [users, setUsers] = useState<UserRow[]>([]);
  const [addingSeats, setAddingSeats] = useState(false);
  const [seatsBooked, setSeatsBooked] = useState(false);

  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  const [period, setPeriod] = useState<BillingPeriod>('monthly');

  // 1 Seat = 1 Benutzerlizenz — die Mengen ergeben sich aus der Zuordnung je Benutzer
  const assigned = users.map((u) => licenseAssignments[u.id]).filter(Boolean);
  const recruitingQty = assigned.filter((p) => p === 'saia_recruiting').length;
  const salesQty = assigned.filter((p) => p === 'saia_sales').length;
  const bundleQty = assigned.filter((p) => p === 'saia_bundle').length;
  const allAssigned = users.length > 0 && assigned.length === users.length;
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const checkoutContainerRef = useRef<HTMLDivElement>(null);
  const embeddedCheckoutRef = useRef<{ destroy: () => void } | null>(null);

  // Eingebettetes Stripe-Checkout beim Verlassen aufräumen
  useEffect(() => {
    return () => {
      embeddedCheckoutRef.current?.destroy();
      embeddedCheckoutRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [res, usersRes] = await Promise.all([
          API_V1.get('/licenses/current/full'),
          API_V1.get('/tenants/current/users').catch(() => ({ data: { users: [] } })),
        ]);
        if (cancelled) return;
        // Menü (Recruiting/Sales) liest die Lizenzen aus dem User-Store, der nur beim App-Start
        // geladen wurde — nach Kauf/Nachbuchen fehlten die Bereiche bis zum Neuladen.
        const fullLicenses = Array.isArray(res.data) ? res.data : res.data?.licenses ?? [];
        useUserStore.getState().setLicenses(fullLicenses);
        setUsers(
          (usersRes.data?.users || []).map((u: any) => ({
            id: u.id,
            name: [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email,
            email: u.email,
          }))
        );
        const rows: LicenseRow[] = (res.data || [])
          .filter((l: any) => ['saia_recruiting', 'saia_sales'].includes(l.product))
          .map((l: any) => ({
            id: l.id || l._id,
            product: l.product,
            linkedinAccountId: l.linkedinAccountId || null,
            isExpired: !!l.isExpired,
          }));
        setLicenses(rows);
      } catch {
        if (!cancelled) setLicenses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataVersion]);

  const activeLicenses = licenses.filter((l) => !l.isExpired);
  const hasLicense = activeLicenses.length > 0;

  // Nachkauf: geplante Benutzer (Schritt „Benutzer") gegen vorhandene Lizenzen.
  // Ein Bundle-Seat zählt für Recruiting UND Sales; nachgebucht wird einzeln.
  const ownedRecruiting = activeLicenses.filter((l) => l.product === 'saia_recruiting').length;
  const ownedSales = activeLicenses.filter((l) => l.product === 'saia_sales').length;
  const missingRecruiting = Math.max(0, recruitingQty + bundleQty - ownedRecruiting);
  const missingSales = Math.max(0, salesQty + bundleQty - ownedSales);
  const missingSeats = missingRecruiting + missingSales;
  const missingProducts = [
    ...(missingRecruiting > 0 ? [{ product: 'saia_recruiting', quantity: missingRecruiting }] : []),
    ...(missingSales > 0 ? [{ product: 'saia_sales', quantity: missingSales }] : []),
  ];

  const handleAddSeats = async () => {
    setAddingSeats(true);
    setError(null);
    try {
      await API_V1.post('/payments/seats', { seats: missingProducts });
      setSeatsBooked(true);
      // Die Lizenzen legt der Stripe-Webhook an — kurz warten, dann neu laden
      setTimeout(() => bumpDataVersion(), 4000);
    } catch (e: any) {
      if (e?.response?.status === 409) {
        // Kein laufendes Abo (z. B. Lizenz ohne Stripe) → regulärer Checkout für die fehlenden Seats
        await handleCheckout(missingProducts);
      } else {
        setError(
          e?.response?.data?.message ||
            (isDe ? 'Seats konnten nicht gebucht werden.' : 'Could not book the seats.')
        );
      }
    } finally {
      setAddingSeats(false);
    }
  };

  const handleCheckout = async (productsOverride?: Array<{ product: string; quantity: number }>) => {
    setStarting(true);
    setError(null);
    try {
      // App läuft unter Vites base (/dashboard) — ohne das Präfix matcht der Router nach dem
      // Checkout keine Route und zeigt eine weiße Seite.
      const base = `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}`;
      const products = productsOverride ?? [
        ...(recruitingQty > 0 ? [{ product: 'saia_recruiting', quantity: recruitingQty }] : []),
        ...(salesQty > 0 ? [{ product: 'saia_sales', quantity: salesQty }] : []),
        ...(bundleQty > 0 ? [{ product: 'saia_bundle', quantity: bundleQty }] : []),
      ];
      if (products.length === 0) throw new Error('no products');
      const res = await API_V1.post('/payments/checkout-session', {
        period,
        products,
        uiMode: 'embedded',
        successUrl: `${base}/ai-chat?checkout=success`,
        cancelUrl: `${base}/ai-chat?checkout=cancelled`,
      });
      const clientSecret = res.data?.clientSecret;
      if (!clientSecret) throw new Error('no client secret');

      const stripe = await loadStripe(config.stripe_pk);
      if (!stripe) throw new Error('stripe failed to load');

      embeddedCheckoutRef.current?.destroy();
      const checkout = await stripe.initEmbeddedCheckout({ clientSecret });
      embeddedCheckoutRef.current = checkout;
      setCheckoutOpen(true);
      // Container ist erst nach dem State-Update im DOM
      requestAnimationFrame(() => {
        if (checkoutContainerRef.current) checkout.mount(checkoutContainerRef.current);
      });
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error?.message ||
          (isDe
            ? 'Checkout konnte nicht gestartet werden — sind die Stripe-Preise konfiguriert?'
            : 'Could not start checkout — are the Stripe prices configured?')
      );
    } finally {
      setStarting(false);
    }
  };

  const selectPeriod = (next: BillingPeriod) => {
    if (next === period) return;
    // Ein offenes Zahlungsformular gehört zur alten Periode — schließen, damit neu gestartet wird
    if (checkoutOpen) closeCheckout();
    setPeriod(next);
  };

  const closeCheckout = () => {
    embeddedCheckoutRef.current?.destroy();
    embeddedCheckoutRef.current = null;
    setCheckoutOpen(false);
  };

  const monthlyBaseTotal =
    recruitingQty * MONTHLY_BASE.saia_recruiting +
    salesQty * MONTHLY_BASE.saia_sales +
    bundleQty * MONTHLY_BASE.saia_bundle;
  const selectedMeta = PERIOD_META.find((m) => m.key === period)!;
  const totalPerPeriod = monthlyBaseTotal * selectedMeta.months * (1 - selectedMeta.discount);
  const effectivePerMonth = monthlyBaseTotal * (1 - selectedMeta.discount);

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {hasLicense ? (
        <Box className={cx(classes.gapBanner, classes.doneBanner)}>
          <span className={cx(classes.gapDot, classes.doneDot)}>✓</span>
          <Typography variant="caption" className={cx(classes.gapText, classes.doneText)}>
            {isDe
              ? `${activeLicenses.length} aktive Lizenz(en) im Workspace`
              : `${activeLicenses.length} active license(s) in the workspace`}
          </Typography>
        </Box>
      ) : (
        <Box className={classes.gapBanner}>
          <span className={classes.gapDot}>!</span>
          <Typography variant="caption" className={classes.gapText}>
            {isDe
              ? 'Fehlt in diesem Schritt: eine SAIA-Lizenz (Recruiting oder Sales)'
              : 'Missing in this step: a SAIA license (recruiting or sales)'}
          </Typography>
        </Box>
      )}

      <Box className={classes.card}>
        <Typography variant="caption" className={classes.sectionLabel}>
          {isDe ? 'SAIA-Lizenz je Benutzer zuordnen' : 'Assign a SAIA license to each user'}
        </Typography>
        <Box className={classes.assignList}>
          {users.map((u) => (
            <Box key={u.id} className={cx(classes.assignRow, !licenseAssignments[u.id] && 'open')}>
              <Box className={classes.assignUser}>
                <Typography variant="body2" className={classes.seatName}>{u.name}</Typography>
                <Typography variant="caption" color="textSecondary">{u.email}</Typography>
              </Box>
              <Box className={classes.assignOptions}>
                {LICENSE_OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={cx(classes.assignOption, licenseAssignments[u.id] === o.key && 'selected')}
                    onClick={() => setLicenseAssignment(u.id, o.key)}
                  >
                    {o.key === 'saia_bundle' ? 'Kombi' : o.label.replace('SAIA ', '')}
                    <Tooltip title={isDe ? o.infoDe : o.infoEn} arrow placement="top">
                      <InfoOutlinedIcon className={classes.infoIcon} />
                    </Tooltip>
                  </button>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
        {!allAssigned && (
          <Typography variant="caption" className={classes.assignHint}>
            {isDe
              ? 'Bitte wähle für jeden Benutzer eine Lizenz — erst dann geht es zur Zahlungsperiode.'
              : 'Please choose a license for every user — then the billing period follows.'}
          </Typography>
        )}
        <Box>
          <button type="button" className={classes.linkButton} onClick={() => setSidePanel('team')}>
            {isDe ? 'Benutzer hinzufügen oder entfernen' : 'Add or remove users'}
          </button>
        </Box>
        {allAssigned && !hasLicense && (
          <>
        <Typography variant="caption" className={classes.sectionLabel}>
          {isDe ? 'Zahlungsperiode' : 'Billing period'}
        </Typography>
        <Box className={classes.periodRow}>
          {PERIOD_META.map((m) => {
            const perMonth = monthlyBaseTotal * (1 - m.discount);
            return (
              <Box
                key={m.key}
                className={cx(classes.periodOption, period === m.key && 'selected')}
                onClick={() => selectPeriod(m.key)}
              >
                <Box className={classes.periodHead}>
                  <Typography variant="body2" className={classes.periodLabel}>
                    {isDe ? m.de : m.en}
                  </Typography>
                  {m.badge && <span className={classes.periodBadge}>{m.badge}</span>}
                </Box>
                <Typography variant="caption" className={classes.periodPrice}>
                  {monthlyBaseTotal > 0
                    ? `${euro(perMonth)} / ${isDe ? 'Monat' : 'month'}`
                    : `${euro(MONTHLY_BASE.saia_recruiting * (1 - m.discount))} / ${isDe ? 'Monat je Seat' : 'month per seat'}`}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {monthlyBaseTotal > 0 && (
          <Box className={classes.priceSummary}>
            <Typography variant="body2" className={classes.priceSummaryMain}>
              {isDe
                ? `Nach 14 Tagen Test: ${euro(totalPerPeriod)} ${
                    period === 'monthly' ? 'pro Monat' : period === 'quarterly' ? 'pro Quartal' : 'pro Jahr'
                  }`
                : `After the 14-day trial: ${euro(totalPerPeriod)} ${
                    period === 'monthly' ? 'per month' : period === 'quarterly' ? 'per quarter' : 'per year'
                  }`}
            </Typography>
            {period !== 'monthly' && (
              <Typography variant="caption" color="textSecondary">
                {isDe
                  ? `entspricht ${euro(effectivePerMonth)} pro Monat statt ${euro(monthlyBaseTotal)}`
                  : `equals ${euro(effectivePerMonth)} per month instead of ${euro(monthlyBaseTotal)}`}
              </Typography>
            )}
            <Typography variant="caption" color="textSecondary">
              {isDe ? 'Alle Preise inkl. 19 % MwSt.' : 'All prices incl. 19 % VAT.'}
            </Typography>
          </Box>
        )}

        <Box className={classes.trialNote}>
          <Typography variant="caption" className={classes.trialTitle}>
            {isDe ? '14 Tage kostenlos testen' : '14-day free trial'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {isDe
              ? 'Zahlung per Kreditkarte oder SEPA-Lastschrift — die Daten trägst du direkt hier im Formular ein. Nach 14 Tagen wird automatisch abgerechnet; kündbar jederzeit im Kundenportal.'
              : 'Pay by credit card or SEPA direct debit — you enter the details right here in the form. After 14 days, billing is automatic; cancel anytime in the customer portal.'}
          </Typography>
        </Box>
          </>
        )}
      </Box>

      {checkoutOpen && (
        <Box className={classes.card}>
          <Box className={classes.checkoutHeader}>
            <Typography variant="caption" className={classes.sectionLabel}>
              {isDe ? 'Zahlungsdaten (Karte oder SEPA)' : 'Payment details (card or SEPA)'}
            </Typography>
            <button type="button" className={classes.cancelLink} onClick={closeCheckout}>
              {isDe ? 'Abbrechen' : 'Cancel'}
            </button>
          </Box>
          <div ref={checkoutContainerRef} className={classes.checkoutContainer} />
        </Box>
      )}

      {error && (
        <Typography variant="caption" className={classes.error}>
          {error}
        </Typography>
      )}

      {/* Mehr Benutzer geplant als Lizenzen da → Seats im bestehenden Abo nachbuchen */}
      {!checkoutOpen && hasLicense && missingSeats > 0 && !seatsBooked && (
        <Box className={classes.footer}>
          <Typography variant="caption" className={classes.footerHint}>
            {isDe
              ? `${missingSeats} Benutzer ohne Lizenz. Im Testzeitraum sind weitere Seats bis zu dessen Ende kostenlos, danach anteilig mit der nächsten Rechnung.`
              : `${missingSeats} user(s) without a license. During the trial extra seats are free until it ends, afterwards prorated on the next invoice.`}
          </Typography>
          <button type="button" className={classes.saveButton} onClick={handleAddSeats} disabled={addingSeats || starting}>
            {addingSeats || starting
              ? isDe ? 'Bucht…' : 'Booking…'
              : isDe ? `${missingSeats} Seat(s) hinzufügen` : `Add ${missingSeats} seat(s)`}
          </button>
        </Box>
      )}

      {/* Genau ein Knopf: ohne Lizenz der Checkout, mit Lizenz „Weiter" (vorher standen beide da). */}
      {!checkoutOpen && done && onNext && !(hasLicense && missingSeats > 0 && !seatsBooked) && (
        <Box className={classes.footer}>
          <Typography variant="caption" className={classes.footerHint}>
            {isDe
              ? seatsBooked
                ? 'Seats gebucht — die Lizenzen erscheinen in wenigen Sekunden.'
                : 'Lizenz aktiv. Weitere Seats buchst du jederzeit unter Account → Lizenzen.'
              : seatsBooked
                ? 'Seats booked — the licenses appear in a few seconds.'
                : 'License active. You can book more seats any time under Account → Licenses.'}
          </Typography>
          <button type="button" className={classes.saveButton} onClick={onNext}>
            {isDe ? 'Weiter' : 'Next'}
          </button>
        </Box>
      )}
      {!checkoutOpen && !(done && onNext) && (
        <Box className={classes.footer}>
          <Typography variant="caption" className={classes.footerHint}>
            {isDe
              ? allAssigned
                ? 'Startet den Checkout für die zugeordneten Lizenzen.'
                : 'Erst jedem Benutzer eine Lizenz zuordnen.'
              : allAssigned
                ? 'Starts the checkout for the assigned licenses.'
                : 'First assign a license to every user.'}
          </Typography>
          <button type="button" className={classes.saveButton} onClick={() => handleCheckout()} disabled={starting || !allAssigned}>
            {starting
              ? isDe ? 'Öffnet…' : 'Opening…'
              : isDe ? 'Kostenlos testen' : 'Start free trial'}
          </button>
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    maxWidth: 620,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
  },
  gapBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 14px',
    borderRadius: 12,
    backgroundColor: dc.warnBg,
    border: `1px solid ${dc.warnBorder}`,
  },
  gapDot: {
    flexShrink: 0,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: dc.warn,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
  },
  gapText: {
    color: dc.warnText,
    lineHeight: 1.4,
  },
  doneBanner: {
    backgroundColor: '#EAFBF4',
    border: '1px solid #C6EEDD',
  },
  doneDot: {
    backgroundColor: dc.success,
  },
  doneText: {
    color: '#0B7050',
  },
  card: {
    backgroundColor: dc.surface,
    border: `1px solid ${dc.border}`,
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: dc.muted,
  },
  licenseRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
  },
  licenseDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: dc.success,
    flexShrink: 0,
  },
  licenseName: {
    flex: 1,
    fontWeight: 600,
    color: dc.ink,
  },
  seatSummary: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  seatRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  seatCount: {
    minWidth: 34,
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 13,
    color: dc.primary,
    backgroundColor: dc.primaryHoverBg,
    border: `1px solid ${dc.border}`,
    borderRadius: 8,
    padding: '3px 6px',
  },
  seatName: {
    fontWeight: 600,
    color: dc.ink,
  },
  linkButton: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12.5,
    fontWeight: 600,
    color: dc.primary,
    padding: 0,
    textDecoration: 'underline',
    '&:hover': {
      opacity: 0.8,
    },
  },
  assignList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  assignRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    padding: '8px 10px',
    borderRadius: 10,
    border: `1px solid ${dc.border}`,
    '&.open': {
      borderColor: '#E0A100',
      backgroundColor: 'rgba(224, 161, 0, 0.06)',
    },
  },
  assignUser: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  assignOptions: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  assignOption: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    border: `1px solid ${dc.border}`,
    borderRadius: 999,
    padding: '5px 10px',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12.5,
    fontWeight: 600,
    color: dc.ink,
    '&.selected': {
      borderColor: dc.primary,
      backgroundColor: dc.primaryHoverBg,
      color: dc.primary,
    },
  },
  infoIcon: {
    fontSize: 14,
    color: dc.muted,
  },
  assignHint: {
    color: '#9A6B00',
    fontWeight: 600,
  },
  periodRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 10,
  },
  periodOption: {
    border: `1px solid ${dc.border}`,
    borderRadius: 12,
    padding: '10px 12px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    '&.selected': {
      borderColor: dc.primary,
      backgroundColor: dc.primaryHoverBg,
    },
  },
  periodHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  },
  periodLabel: {
    fontWeight: 700,
    fontSize: 13,
    color: dc.ink,
  },
  periodBadge: {
    fontSize: 10.5,
    fontWeight: 700,
    color: '#0B7050',
    backgroundColor: '#EAFBF4',
    border: '1px solid #C6EEDD',
    borderRadius: 999,
    padding: '2px 7px',
    whiteSpace: 'nowrap',
  },
  periodPrice: {
    color: dc.muted,
  },
  priceSummary: {
    backgroundColor: dc.bg,
    border: `1px solid ${dc.border}`,
    borderRadius: 12,
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  priceSummaryMain: {
    fontWeight: 700,
    color: dc.ink,
  },
  trialNote: {
    backgroundColor: dc.bg,
    border: `1px solid ${dc.border}`,
    borderRadius: 12,
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  trialTitle: {
    fontWeight: 700,
    color: dc.ink,
  },
  checkoutHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cancelLink: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    color: dc.muted,
    '&:hover': {
      color: dc.primary,
    },
  },
  checkoutContainer: {
    minHeight: 400,
  },
  error: {
    color: theme.palette.error.main,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  footerHint: {
    color: dc.muted,
    lineHeight: 1.4,
  },
  saveButton: {
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    backgroundColor: dc.primary,
    borderRadius: 10,
    padding: '9px 18px',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
}));

export default LicensePanel;
